/**
 * How long after its end a session still counts as running: late finishers,
 * Q&A, people reading the last slide. Server, core and builder all decide
 * "is this session over" on this one rule, so a registration the server still
 * treats as upcoming is never shown as finished, and vice versa.
 */
export const EVENT_SESSION_GRACE_MS = 2 * 60 * 60 * 1000;

export interface EventSessionWindow {
  startDate: Date | string;
  endDate?: Date | string | null;
}

/** The window a registration occupies: the variant's dates, else the event's own. */
export function sessionWindow(
  event: EventSessionWindow,
  variant?: EventSessionWindow | null
): EventSessionWindow {
  if (!variant) return { startDate: event.startDate, endDate: event.endDate };
  return {
    startDate: variant.startDate,
    endDate: variant.endDate ?? variant.startDate,
  };
}

function toTime(date: Date | string) {
  return date instanceof Date ? date.getTime() : Date.parse(date);
}

/** The moment a session stops counting as running. */
export function sessionRunsUntil(window: EventSessionWindow): number {
  return toTime(window.endDate ?? window.startDate) + EVENT_SESSION_GRACE_MS;
}

export function isSessionOver(
  window: EventSessionWindow,
  now = Date.now()
): boolean {
  return now > sessionRunsUntil(window);
}

/**
 * The same question asked of a card's date range (earliest start, latest end
 * across the sessions), which is all a resolved collection member carries.
 */
export function isEventRangeOver(
  range: { startsAt: string; endsAt?: string | null },
  now = Date.now()
): boolean {
  return isSessionOver({ startDate: range.startsAt, endDate: range.endsAt }, now);
}

/** Every session, the event's own date and each variant, is over. */
export function isEventOver(
  event: EventSessionWindow & { variants?: EventSessionWindow[] | null },
  now = Date.now()
): boolean {
  if (!isSessionOver(sessionWindow(event), now)) return false;
  return (event.variants ?? []).every((variant) =>
    isSessionOver(sessionWindow(event, variant), now)
  );
}
